import http from '@/services/http';

const initialState = {
  // The current account API keys list pulled from app.applications.
  apiKeys: null,
};

/**
 * Getter function that sorts the apiKeys up if the key is not disabled.
 * @param state {Object} state for the apiKeys vuex context
 * @return {Array}
 */
function sortedApiKeys(state) {
  return (state.apiKeys || [])
    .slice() // Copy to avoid prohibited external state mutation.
    .sort((a, b) => (a.is_disabled > b.is_disabled ? 1 : -1));
}

export const getters = { sortedApiKeys };

/**
 * Get all api keys for account id and store it in state.
 * @param state { Object } state for the apiKeys vuex context
 * @param commit { Object } commit for the apiKeys vuex context
 * @param accountId { Number } the account id to get the API keys for
 * @return { Object } The raw account details
 */
async function getApiKeys({ state, commit }, accountId) {
  if (state.apiKeys) {
    return state.apiKeys;
  }
  const response = await http.getApiKeys(accountId);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  commit('setApiKeys', response.payload.data);
  return state.apiKeys;
}

/**
 * Updates apiKey in the vuex store and the monolith
 * @param state { Object } state for the apiKeys vuex context
 * @param payload { Object } Payload containing applicationId, accountId, update_values.
 */
async function updateApiKey({ commit }, payload) {
  const response = await http.updateApplication(payload);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  commit('setApiKeys', response.payload.data);
}

/**
 * Regenerate application key for the application. Updates applications.key in monolith.
 * @param state { Object } state for the apiKeys vuex context
 * @param payload { Object } Payload containing applicationId, and accountId.
 */
async function regenerateApplicationKey({ commit }, payload) {
  const response = await http.regenerateApplicationKey(payload);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  commit('setApiKeys', response.payload.data);
}

/**
 * Insert application key for the application to applications table in monolith.
 * @param state { Object } state for the apiKeys vuex context
 * @param payload { Object } Payload containing applicationId, and accountId.
 */
async function insertApplicationKey({ commit }, payload) {
  const response = await http.insertApplicationKey(payload);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  commit('setApiKeys', response.payload.data);
}

/**
 * Resets all apiKeys state
 */
function resetApiKeysState({ commit }) {
  commit('setApiKeys', null);
}

export const actions = {
  getApiKeys, resetApiKeysState, updateApiKey, regenerateApplicationKey, insertApplicationKey,
};

export const mutations = {
  setApiKeys(state, keys) {
    state.apiKeys = keys;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
